/**
 * Main Durin AI client
 */

import { BaseClient } from './BaseClient';
import {
  ClientConfig,
  GenerateOptions,
  TranslateParams,
  RewriteParams,
  SummarizeParams,
  RequestOptions,
  UnifiedResponse,
  StreamingChunk,
  ModelInfo,
  HealthResponse,
  TunnelRequest,
  IntentRequest
} from '../types';
import {
  validatePrompt,
  validateMaxTokens,
  validateModelId,
  validateTranslateParams,
  validateRewriteParams,
  validateSummarizeParams,
  DurinError,
  DurinNetworkError,
  handleHttpError,
  isNetworkError,
  createStreamIterable
} from '../utils';

export class DurinClient extends BaseClient {
  private lastResponseHeaders: Headers | null = null;

  constructor(config: ClientConfig) {
    super(config);
  }

  getLastResponseHeaders(): Headers | null {
    return this.lastResponseHeaders;
  }

  // Health and model discovery methods
  override async healthCheck(): Promise<HealthResponse> {
    return super.healthCheck();
  }

  override async getAvailableModels(): Promise<Record<string, ModelInfo>> {
    return super.getAvailableModels();
  }

  // Convenience methods for model discovery
  async getDefaultModel(): Promise<ModelInfo | null> {
    const models = await this.getAvailableModels();
    const defaultEntry = Object.entries(models).find(([_, info]) => info.default);
    return defaultEntry ? defaultEntry[1] : null;
  }

  async getModelsByProvider(provider: string): Promise<Record<string, ModelInfo>> {
    const models = await this.getAvailableModels();
    return Object.fromEntries(
      Object.entries(models).filter(([_, info]) => info.provider === provider)
    );
  }

  // Generic generation methods
  async generate(prompt: string, options?: GenerateOptions): Promise<UnifiedResponse> {
    validatePrompt(prompt);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: TunnelRequest = { prompt };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    
    return this.tunnel(request);
  }

  async generateStream(
    prompt: string, 
    options?: GenerateOptions,
    signal?: AbortSignal
  ): Promise<AsyncIterable<StreamingChunk>> {
    validatePrompt(prompt);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: TunnelRequest = { prompt };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    if (options?.ndjson) request.ndjson = options.ndjson;
    
    return this.tunnelStream(request, signal);
  }

  // Translation methods
  async translate(
    text: string,
    params: TranslateParams,
    options?: RequestOptions
  ): Promise<UnifiedResponse> {
    validatePrompt(text);
    validateTranslateParams(params);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: IntentRequest = {
      prompt: text,
      parameters: {
        target_language: params.targetLanguage,
        ...(params.sourceLanguage && { source_language: params.sourceLanguage })
      }
    };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    
    return this.intentRequest('/v1/tunnel/intent/translate', request);
  }

  async translateStream(
    text: string,
    params: TranslateParams,
    options?: RequestOptions,
    signal?: AbortSignal
  ): Promise<AsyncIterable<StreamingChunk>> {
    validatePrompt(text);
    validateTranslateParams(params);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: IntentRequest = {
      prompt: text,
      parameters: {
        target_language: params.targetLanguage,
        ...(params.sourceLanguage && { source_language: params.sourceLanguage })
      }
    };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    if (options?.ndjson) request.ndjson = options.ndjson;

    return this.intentRequestStreamWithHeaders('/v1/tunnel/intent/translate', request, signal);
  }

  // Rewrite methods
  async rewrite(
    text: string,
    params: RewriteParams,
    options?: RequestOptions
  ): Promise<UnifiedResponse> {
    validatePrompt(text);
    validateRewriteParams(params);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: IntentRequest = {
      prompt: text,
      parameters: {
        tone: params.tone,
        ...(params.target_text && { target_text: params.target_text })
      }
    };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    
    return this.intentRequest('/v1/tunnel/intent/rewrite', request);
  }

  async rewriteStream(
    text: string,
    params: RewriteParams,
    options?: RequestOptions,
    signal?: AbortSignal
  ): Promise<AsyncIterable<StreamingChunk>> {
    validatePrompt(text);
    validateRewriteParams(params);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: IntentRequest = {
      prompt: text,
      parameters: {
        tone: params.tone,
        ...(params.target_text && { target_text: params.target_text })
      }
    };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    if (options?.ndjson) request.ndjson = options.ndjson;

    return this.intentRequestStreamWithHeaders('/v1/tunnel/intent/rewrite', request, signal);
  }

  /**
   * Intent-based request (streaming) with header capture
   */
  private async intentRequestStreamWithHeaders(
    endpoint: string,
    request: IntentRequest,
    signal?: AbortSignal
  ): Promise<AsyncIterable<StreamingChunk>> {
    const body: any = {
      prompt: request.prompt,
      parameters: request.parameters,
      stream: true,
      ndjson: request.ndjson || false
    };

    // Only include modelId if provided (let backend handle default)
    if (request.modelId || this.defaultModelId) {
      body.modelId = request.modelId || this.defaultModelId;
    }

    // Only include maxTokens if provided
    if (request.maxTokens !== undefined) {
      body.maxTokens = request.maxTokens;
    }

    const url = `${this.baseUrl}${endpoint}`;

    // Create abort controller for timeout if no signal provided
    let controller: AbortController | undefined;
    let timeoutId: NodeJS.Timeout | undefined;

    if (!signal) {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller!.abort(), this.timeout);
      signal = controller.signal;
    }

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: this.getRequestHeaders(),
      body: JSON.stringify(body),
      signal
    };

    try {
      const response = await fetch(url, requestOptions);
      if (timeoutId) clearTimeout(timeoutId);

      // Capture response headers
      this.lastResponseHeaders = response.headers;

      if (!response.ok) {
        let errorData;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorData = await response.json();
          } else {
            errorData = await response.text();
          }
        } catch {
          // Ignore parse errors for error responses
        }

        throw handleHttpError(response, errorData);
      }

      if (!response.body) {
        throw new DurinError('No response body for streaming request');
      }

      return createStreamIterable(response.body, body.ndjson, signal);
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);

      if (error instanceof DurinError) {
        throw error;
      }

      if (isNetworkError(error as Error)) {
        throw new DurinNetworkError(`Failed to connect to streaming endpoint ${url}`, error as Error);
      }

      throw new DurinError(
        `Streaming request failed: ${(error as Error).message}`,
        undefined,
        undefined,
        error as Error
      );
    }
  }

  // Summarize methods
  async summarize(
    text: string,
    params: SummarizeParams,
    options?: RequestOptions
  ): Promise<UnifiedResponse> {
    validatePrompt(text);
    validateSummarizeParams(params);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: IntentRequest = {
      prompt: text,
      parameters: {
        length: params.length,
        ...(params.format && { format: params.format })
      }
    };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    
    return this.intentRequest('/v1/tunnel/intent/summarize', request);
  }

  async summarizeStream(
    text: string,
    params: SummarizeParams,
    options?: RequestOptions,
    signal?: AbortSignal
  ): Promise<AsyncIterable<StreamingChunk>> {
    validatePrompt(text);
    validateSummarizeParams(params);
    validateMaxTokens(options?.maxTokens);
    validateModelId(options?.modelId);

    const request: IntentRequest = {
      prompt: text,
      parameters: {
        length: params.length,
        ...(params.format && { format: params.format })
      }
    };
    if (options?.modelId) request.modelId = options.modelId;
    if (options?.maxTokens) request.maxTokens = options.maxTokens;
    if (options?.ndjson) request.ndjson = options.ndjson;
    
    return this.intentRequestStream('/v1/tunnel/intent/summarize', request, signal);
  }
}